import { jsx, jsxs } from 'react/jsx-runtime';
import { Icon24Close } from '@figma/fpl-icons/icon-24-close';
import { Icon24Progress, Icon24Spinner } from '@figma/fpl-icons/internal';
import clsx from 'clsx';
import { Button } from '../buttons/button.js';
import '../buttons/dialog-trigger-button/dialog-trigger-button.js';
import { IconButton } from '../buttons/icon-button/icon-button.js';
import '../buttons/toggle-button/toggle-button.js';
import { Link } from '../link/link.js';
import { useString } from '../settings/settings.strings.js';
import { useDismissToast, Root } from '../toast-primitive/toast-primitive.js';
import { buttonWrap, count, dismissButton, message, root, animate } from './toast.css.js';
import { AnnouncementPrimitive } from '../announcement-primitive/announcement-primitive.js';

/**
 * Root component for a Toast, which shows a message to the user with optional actions or dismiss buttons.
 * All other `Toast` components should be nested inside `Root`
 *
 * @compose { "root": true, "strictChildren": true }
 * @see https://fpl.figma.design/components/toast/
 */
function ToastRoot({ variant, ...props }) {
    return (jsx(Root, { "data-fpl-component": "", ...props, className: root[variant ?? 'default'] }));
}
ToastRoot.displayName = 'Toast.Root';
/**
 * Text to be displayed inside of `Toast.Root`
 *
 * @compose { "parent": ["Toast.Root"] }
 */
function ToastMessage({ role = 'alert', ...props }) {
    return (jsx(AnnouncementPrimitive, { "data-fpl-component": "", ...props, role: role, className: message }));
}
ToastMessage.displayName = 'Toast.Message';
/**
 * An optional secondary/supplemental portion of a Toast message. This should be a *child* of the Toast.Message.
 *
 * @compose { "parent": ["Toast.Message"] }
 */
function ToastCount(props) {
    return jsx("span", { "data-fpl-component": "", ...props, className: count });
}
ToastCount.displayName = 'Toast.Count';
/**
 * Dismiss button (x) to use inside of a `Toast.Root`.
 *
 * @compose { "parent": ["Toast.Root"] }
 */
function ToastDismissButton(props) {
    const dismiss = useDismissToast();
    const dismissString = useString('dismiss');
    const onClick = () => dismiss('dismiss');
    return (jsx("div", { "data-fpl-component": "", ...props, className: clsx(buttonWrap, dismissButton), children: jsx(IconButton, { "aria-label": dismissString, onClick: onClick, children: jsx(Icon24Close, {}) }) }));
}
ToastDismissButton.displayName = 'Toast.DismissButton';
/**
 * Button to use inside of a `Toast.Root` for a user action.
 *
 * @compose { "parent": ["Toast.Root"], "multi": true }
 */
function ToastActionButton({ children, action, ...props }) {
    const dismiss = useDismissToast();
    const onClickWithDismiss = (...args) => {
        const result = action?.(...args);
        if (result !== false) {
            dismiss('action');
        }
    };
    return (jsx("div", { "data-fpl-component": "", className: buttonWrap, children: jsx(Button, { ...props, onClick: onClickWithDismiss, variant: "secondary", children: children }) }));
}
ToastActionButton.displayName = 'Toast.ActionButton';
/**
 * Link button to use inside of a `Toast.Root` for a user action.
 *
 * @compose { "parent": ["Toast.Root"], "multi": true }
 */
function ToastLink(props) {
    return (jsx("div", { "data-fpl-component": "", className: buttonWrap, children: jsx(Link.Button, { ...props, variant: "secondary" }) }));
}
ToastLink.displayName = 'Toast.Link';
/**
 * Loading spinner to be used inside of a `Toast.Root` when the toast is in a loading state.
 *
 * @compose { "parent": ["Toast.Root"], "oneOf": "progress" }
 */
function ToastSpinner(props) {
    return (jsx("div", { "data-fpl-component": "", className: animate, children: jsx(Icon24Spinner, { ...props }) }));
}
ToastSpinner.displayName = 'Toast.Spinner';
/**
 * Progress bar to be used inside of a `Toast.Root` when the toast is in a loading state with intermediate progress.
 *
 * @compose { "parent": ["Toast.Root"], "oneOf": "progress" }
 */
function ToastProgress({ progressFraction, ...props }) {
    let arcFill;
    const fraction = Math.min(1, Math.max(0, progressFraction));
    /* eslint-disable fpl/fpl-data-attr */
    if (fraction === 0) {
        arcFill = null;
    }
    else if (fraction === 1) {
        arcFill = jsx("circle", { cx: "12", cy: "12", r: "5", fill: "var(--fpl-icon-color, var(--color-icon))" });
    }
    else {
        const [x, y] = pointOnCircle((0.25 - fraction) * 2 * Math.PI, 5, 12, 12);
        const largeArcFlag = +(fraction > 0.5);
        arcFill = (jsx("path", { d: `M12 7A5 5 0 ${largeArcFlag} 1 ${x} ${y} L12 12 12 7`, fill: "var(--fpl-icon-color, var(--color-icon))" }));
    }
    /* eslint-enable fpl/fpl-data-attr */
    return (jsxs("svg", { "data-fpl-component": true, ...props, width: "24", height: "24", viewBox: "0 0 24 24", children: [arcFill, jsx(Icon24Progress, {})] }));
}
ToastProgress.displayName = 'Toast.Progress';
function pointOnCircle(theta, radius, originX, originY) {
    return [originX + Math.cos(theta) * radius, originY - Math.sin(theta) * radius];
}

export { ToastActionButton as ActionButton, ToastCount as Count, ToastDismissButton as DismissButton, ToastLink as Link, ToastMessage as Message, ToastProgress as Progress, ToastRoot as Root, ToastSpinner as Spinner };
//# sourceMappingURL=toast.js.map
